import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea, EstadoVazio } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { ROTULO_DO_TIPO, lerControlados, marcarConferido } from '../../controlados';
import type {
  ControladoVendido,
  ControladosDoDia as Dados,
  TipoDoArmario,
} from '../../controlados';
import './controlados.css';

/**
 * CONTROLADOS DO DIA (dono, 26/10/2026): "Bloco Controlados — Ariane: mostrar medicamentos
 * controlados vendidos no dia anterior".
 *
 * A farmacêutica abre e vê o que saiu do ARMÁRIO no dia anterior — controlados, antibióticos e
 * déficit de atenção (a mesma regra do Giro, inclusive pela molécula) — com quantidade, hora,
 * cupom e quem vendeu. Marca cada item conferido; o dia fica salvo com o nome dela e a hora.
 * Dá para voltar a qualquer dia e imprimir.
 */

const somar = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const diaLongo = (dia: string) =>
  `${SEMANA[new Date(`${dia}T12:00:00Z`).getUTCDay()] ?? ''}, ${dia.slice(8, 10)}/${dia.slice(5, 7)}`;
const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
const qtd = (n: number) => (Number.isInteger(n) ? String(n) : n.toLocaleString('pt-BR'));

const ORDEM_DOS_TIPOS: readonly TipoDoArmario[] = [
  'CONTROLADO',
  'MOLECULA',
  'ANTIBIOTICO',
  'DEFICIT',
];

export function ControladosDoDia({
  sessao,
  parametros,
  rota = 'controlados',
}: {
  sessao: Sessao;
  parametros: URLSearchParams;
  rota?: string;
}) {
  const pedido = parametros.get('dia');
  const [d, setD] = useState<Dados | null>(null);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');
  const [soPendentes, setSoPendentes] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);

  const carregar = useCallback(() => {
    setErro(false);
    void lerControlados(pedido).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [pedido]);
  useEffect(() => {
    setD(null);
    carregar();
  }, [carregar]);

  const irPara = (dia: string | null) => {
    window.location.hash = `#/${rota}${dia ? `?dia=${dia}` : ''}`;
  };

  const filtrados = useMemo(() => {
    if (!d) return [];
    const b = busca.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return d.itens.filter((i) => {
      if (soPendentes && i.conferidoEm) return false;
      if (!b) return true;
      const alvo = `${i.descricao} ${i.principioAtivo ?? ''} ${i.codigo ?? ''} ${i.vendas
        .map((v) => `${v.vendedor ?? ''} ${v.cupom ?? ''}`)
        .join(' ')}`
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
      return alvo.includes(b);
    });
  }, [d, busca, soPendentes]);

  if (erro) return <ErroDeArea oQue="os controlados do dia" aoTentar={carregar} />;
  if (!d)
    return (
      <section className="ct" aria-busy="true">
        <div className="ct-carregando">Carregando o armário…</div>
      </section>
    );

  const marcar = async (i: ControladoVendido) => {
    const conferido = !i.conferidoEm;
    setSalvando(i.chave);
    // otimista: o visto aparece na hora
    setD((atual) =>
      atual
        ? {
            ...atual,
            conferidos: atual.conferidos + (conferido ? 1 : -1),
            itens: atual.itens.map((x) =>
              x.chave === i.chave
                ? {
                    ...x,
                    conferidoEm: conferido ? new Date().toISOString() : null,
                    conferidoPor: conferido ? 'você' : null,
                  }
                : x,
            ),
          }
        : atual,
    );
    const r = await marcarConferido(sessao, { dia: d.dia, chave: i.chave, conferido });
    setSalvando(null);
    if (r.tipo !== 'confirmada') {
      setAviso(
        r.tipo === 'recusada' ? r.mensagem : 'A internet caiu — confira a lista (recarreguei).',
      );
      carregar();
    }
  };

  const total = d.itens.reduce((s, i) => s + i.quantidade, 0);
  const pendentes = d.itens.length - d.conferidos;
  const ontem = somar(d.hoje, -1);
  const porTipo = ORDEM_DOS_TIPOS.map((t) => ({
    tipo: t,
    itens: filtrados.filter((i) => i.tipo === t),
  })).filter((g) => g.itens.length > 0);

  return (
    <section className="ct">
      <header className="ct-cabeca">
        <span className="ct-selo" aria-hidden="true">
          <Icone nome="pill" tamanho={22} traco={1.6} />
        </span>
        <div>
          <h1>Controlados do dia</h1>
          <p className="ct-sub">
            O que saiu do armário: controlados, antibióticos e déficit de atenção vendidos no dia —
            para conferir com as receitas retidas.
          </p>
        </div>
      </header>

      <div className="ct-barra ct-nao-imprime">
        <div className="ct-dia">
          <button
            type="button"
            className="ct-btn-icone"
            onClick={() => irPara(somar(d.dia, -1))}
            aria-label="Dia anterior"
          >
            <Icone nome="arrow-l" tamanho={16} />
          </button>
          <strong>{d.dia === ontem ? `Ontem · ${diaLongo(d.dia)}` : diaLongo(d.dia)}</strong>
          <button
            type="button"
            className="ct-btn-icone"
            onClick={() => irPara(somar(d.dia, 1))}
            disabled={d.dia >= d.hoje}
            aria-label="Dia seguinte"
          >
            <Icone nome="arrow" tamanho={16} />
          </button>
          {d.dia !== ontem && (
            <button type="button" className="ct-btn ct-btn-sec" onClick={() => irPara(null)}>
              Ontem
            </button>
          )}
          <input
            type="date"
            className="ct-data"
            value={d.dia}
            max={d.hoje}
            onChange={(e) => e.target.value && irPara(e.target.value)}
            aria-label="Escolher o dia"
          />
        </div>
        <button type="button" className="ct-btn ct-btn-sec" onClick={() => window.print()}>
          <Icone nome="down" tamanho={15} />
          Imprimir
        </button>
      </div>

      <div className="ct-titulo-impressao">
        <strong>CONTROLADOS VENDIDOS</strong>
        <span>{diaLongo(d.dia)}</span>
      </div>

      {d.leitura.aviso && (
        <p className="ct-aviso ct-aviso-atencao" role="status">
          <Icone nome="warn" tamanho={15} />
          {d.leitura.aviso} A lista pode crescer quando as vendas desse dia terminarem de chegar.
        </p>
      )}
      {aviso && (
        <p className="ct-aviso ct-aviso-erro ct-nao-imprime" role="alert">
          {aviso}
          <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
            <Icone nome="x" tamanho={14} />
          </button>
        </p>
      )}

      <div className="ct-kpis">
        <div className="ct-kpi">
          <span>Produtos</span>
          <strong>{d.itens.length}</strong>
        </div>
        <div className="ct-kpi">
          <span>Unidades</span>
          <strong>{qtd(total)}</strong>
        </div>
        <div className={`ct-kpi${pendentes === 0 && d.itens.length > 0 ? ' ct-kpi-ok' : ''}`}>
          <span>Conferidos</span>
          <strong>
            {d.conferidos} de {d.itens.length}
          </strong>
        </div>
      </div>

      {d.itens.length === 0 ? (
        <EstadoVazio
          icone="pill"
          titulo="Nenhum controlado vendido neste dia"
          texto="Nada de controlado, antibiótico ou déficit de atenção saiu do armário nas vendas do HOS deste dia."
        />
      ) : (
        <>
          <div className="ct-filtros ct-nao-imprime">
            <label className="ct-busca">
              <Icone nome="search" tamanho={15} />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar remédio, molécula, vendedor ou cupom"
                aria-label="Buscar"
              />
            </label>
            <label className="ct-check">
              <input
                type="checkbox"
                checked={soPendentes}
                onChange={(e) => setSoPendentes(e.target.checked)}
              />
              Só os que faltam conferir ({pendentes})
            </label>
          </div>

          {porTipo.length === 0 && <p className="ct-mudo">Nada com esse filtro.</p>}
          {porTipo.map((g) => (
            <section key={g.tipo} className="ct-grupo">
              <h2>
                {ROTULO_DO_TIPO[g.tipo]}
                <small>
                  {g.itens.length} produto{g.itens.length === 1 ? '' : 's'}
                </small>
              </h2>
              <ul className="ct-lista">
                {g.itens.map((i) => (
                  <li key={i.chave} className={i.conferidoEm ? 'ct-item ct-item-ok' : 'ct-item'}>
                    <button
                      type="button"
                      className="ct-visto"
                      onClick={() => void marcar(i)}
                      disabled={salvando === i.chave}
                      aria-pressed={!!i.conferidoEm}
                      aria-label={`${i.conferidoEm ? 'Desmarcar' : 'Marcar'} ${i.descricao} como conferido`}
                    >
                      {i.conferidoEm ? <Icone nome="check" tamanho={18} /> : null}
                    </button>
                    <div className="ct-item-corpo">
                      <div className="ct-item-topo">
                        <strong>{i.descricao}</strong>
                        <span className="ct-qtd">{qtd(i.quantidade)} un.</span>
                      </div>
                      <div className="ct-item-sub">
                        {i.codigo && <span>cód. {i.codigo}</span>}
                        {i.principioAtivo && <span>{i.principioAtivo}</span>}
                        {i.tipo === 'MOLECULA' && i.classificacao && (
                          <span title="O HOS classifica esta dosagem de outro jeito, mas a molécula é do armário">
                            HOS: {i.classificacao}
                          </span>
                        )}
                      </div>
                      <ul className="ct-vendas">
                        {i.vendas.map((v, k) => (
                          <li key={`${v.instante}${String(k)}`}>
                            <span className="ct-hora">{hora(v.instante)}</span>
                            <span>{v.vendedor ?? 'sem vendedor'}</span>
                            {v.cupom && <span className="ct-cupom">cupom {v.cupom}</span>}
                            <span className="ct-qtd-venda">{qtd(v.quantidade)} un.</span>
                          </li>
                        ))}
                      </ul>
                      {i.conferidoEm && (
                        <small className="ct-conferido">
                          Conferido por {i.conferidoPor ?? '—'} às {hora(i.conferidoEm)}
                        </small>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </section>
  );
}
